import { Create, Edit, Show, Datagrid, List, TextField, TextInput, SelectInput, SimpleForm, ArrayField, ArrayInput, SimpleFormIterator, required, FunctionField, ShowButton, EditButton, AutocompleteInput, useGetList, Filter, TopToolbar, CreateButton } from "react-admin";
import React from "react";
import { Box, Typography } from "@mui/material";
import AccountHolderBeneficiaries from "../components/accountHolderBeneficiaries";
import AccountHolderImport from "../components/accountHolderImport";
import ListPagination from "../uiComponents/ListPagination";
import { BANK_CHOICES, DEFAULT_BANK } from "../helpers/banks";

// The backend only filters on exact values, so a free-text search box cannot
// narrow the list server-side - which is why the old SearchInput did nothing.
// Like the record filter, every holder is loaded and narrowed on the client as
// the user types; the text searched over is the holder name plus each
// beneficiary's name and ET number, so one box finds a holder by any of them.
// Choosing a suggestion sets the exact `_id` filter the server does understand.
const AccountHolderSearchInput = props => {
    const { data } = useGetList('AccountHolder', {
        pagination: { page: 1, perPage: 1000 },
    });
    const choices = data || [];

    // The option label carries the holder's beneficiaries as well as their name,
    // so a search that matched on a beneficiary shows which holder to pick - two
    // holders can share a name, and the point of searching by a child's ET number
    // is usually to reach the parent who receives their allowance.
    const beneficiarySummary = holder =>
        (holder.beneficiaries || [])
            .map(beneficiary => `${beneficiary.registrationId || ''} ${beneficiary.name || ''}`.trim())
            .filter(Boolean)
            .join(', ');

    const optionText = holder => {
        const summary = beneficiarySummary(holder);
        return summary ? `${holder.name || ''} [${summary}]` : (holder.name || '');
    };

    const searchText = holder =>
        [
            holder.name,
            ...(holder.beneficiaries || []).flatMap(beneficiary => [
                beneficiary.registrationId,
                beneficiary.name,
                beneficiary.fatherName,
            ]),
        ].filter(Boolean).join(' ').toLowerCase();

    const matchSuggestion = (filterValue, choice) => {
        const value = (filterValue || '').trim().toLowerCase();
        if (!value) return true;
        return searchText(choice).includes(value);
    };

    return (
        <AutocompleteInput
            source="_id"
            label="Search holder or beneficiary"
            choices={choices}
            optionValue="id"
            optionText={optionText}
            matchSuggestion={matchSuggestion}
            allowEmpty
            emptyText=""
            style={{ minWidth: 360 }}
            {...props}
        />
    );
};

const AccountHolderFilter = (props) => (
    <Filter {...props} alwaysOn>
        <AccountHolderSearchInput alwaysOn />
    </Filter>
);

// react-admin replaces the list with its empty state when there is nothing yet,
// and that state only offers a plain Add button - so the import is repeated here,
// or it would be unreachable exactly when a fresh install needs it most.
const AccountHoldersEmpty = () => (
    <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" gutterBottom>No account holders yet</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
            Add one by hand, or import a CSV of holders and their beneficiaries.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <CreateButton />
            <AccountHolderImport />
        </Box>
    </Box>
);

const money = value =>
    (typeof value === 'number' ? `${value.toLocaleString('en-US')} ETB` : '—');

const beneficiaryLabel = beneficiary =>
    `${beneficiary.registrationId || ''} ${beneficiary.name || ''} ${beneficiary.fatherName || ''}`.trim();

// Who is paid through this holder, and the standing amount each carries. The
// allowances arrive on the holder record itself (see the finance-guarded
// `beneficiaries` field on the AccountHolder type), so no extra request is made
// per row.
const BeneficiariesField = ({ record }) => {
    const beneficiaries = record?.beneficiaries || [];
    if (beneficiaries.length === 0) {
        return <Typography variant="body2" color="text.secondary">—</Typography>;
    }
    return (
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {beneficiaries.map(beneficiary => (
                <li key={beneficiary.id}>
                    {beneficiaryLabel(beneficiary)}
                    {typeof beneficiary.monthlyAllowance === 'number' ? ` — ${money(beneficiary.monthlyAllowance)}` : ''}
                </li>
            ))}
        </Box>
    );
};

const MonthlyTotalField = ({ record }) => {
    const beneficiaries = record?.beneficiaries || [];
    const total = beneficiaries.reduce(
        (sum, beneficiary) =>
            sum + (typeof beneficiary.monthlyAllowance === 'number' ? beneficiary.monthlyAllowance : 0),
        0
    );
    return <span>{total > 0 ? money(total) : '—'}</span>;
};

const list = props => (
    <List perPage={50} filters={<AccountHolderFilter />} {...props}
        actions={<TopToolbar><CreateButton /><AccountHolderImport /></TopToolbar>}
        empty={<AccountHoldersEmpty />}
        pagination={<ListPagination />}
        sort={{ field: 'name', order: 'ASC' }}>
        <Datagrid rowClick="show">
            <TextField source="name" />
            <FunctionField label="Bank Accounts" render={record => (record.banks || []).length} />
            <FunctionField label="Beneficiaries" render={record => <BeneficiariesField record={record} />} />
            <FunctionField label="Monthly total" render={record => <MonthlyTotalField record={record} />} />
            <ShowButton />
            <EditButton />
        </Datagrid>
    </List>
);

// Holder names are entered as a single free-text value - the organisation does
// not split them into name/fatherName/grandfatherName the way beneficiaries are.
const AccountHolderForm = () => (
    <>
        <TextInput source="name" validate={[required()]} fullWidth />
        <ArrayInput source="banks" label="Bank Accounts" defaultValue={[{ bankName: DEFAULT_BANK }]}>
            <SimpleFormIterator disableReordering>
                <SelectInput source="bankName" label="Bank" choices={BANK_CHOICES} defaultValue={DEFAULT_BANK} />
                <TextInput source="accountNumber" label="Account Number" validate={[required()]} />
            </SimpleFormIterator>
        </ArrayInput>
    </>
);

const create = props => (
    <Create {...props}>
        <SimpleForm redirect="list">
            <AccountHolderForm />
        </SimpleForm>
    </Create>
);

const edit = props => (
    <Edit {...props}>
        <SimpleForm redirect="list">
            <AccountHolderForm />
        </SimpleForm>
    </Edit>
);

const show = props => (
    <Show {...props} queryOptions={{ refetchOnMount: true }}>
        <SimpleForm>
            <TextField source="name" />
            <ArrayField source="banks" label="Bank Accounts">
                <Datagrid bulkActionButtons={false} rowClick={false}>
                    <TextField source="bankName" label="Bank" />
                    <TextField source="accountNumber" label="Account Number" />
                </Datagrid>
            </ArrayField>
        </SimpleForm>
        <AccountHolderBeneficiaries />
    </Show>
);

const AccountHolder ={ list, create, edit, show };

export default AccountHolder;